/**
 * Chunk Load Error Handler
 * Handles "Loading chunk X failed" errors by reloading the page
 * This fixes cache-related issues with code-split chunks
 */

const CHUNK_LOAD_ERROR_PATTERN = /Loading chunk .* failed/i;
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_ATTEMPTS_KEY = 'chunkLoadErrorReloadAttempts';

/**
 * Check if error is a chunk loading error
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;

  const errorMessage = error.message || error.toString();
  return error?.name === 'ChunkLoadError' ||
         CHUNK_LOAD_ERROR_PATTERN.test(errorMessage) ||
         errorMessage.includes('Failed to fetch dynamically imported module') ||
         errorMessage.includes('Importing a module script failed');
};

/**
 * Get current reload attempt count
 */
const getReloadAttempts = () => {
  try {
    return parseInt(sessionStorage.getItem(RELOAD_ATTEMPTS_KEY) || '0', 10);
  } catch {
    return 0;
  }
};

/**
 * Increment reload attempt count
 */
const incrementReloadAttempts = () => {
  try {
    const attempts = getReloadAttempts() + 1;
    sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, attempts.toString());
    return attempts;
  } catch {
    return 1;
  }
};

/**
 * Reset reload attempt count
 */
export const resetReloadAttempts = () => {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
  } catch {
    // Ignore errors
  }
};

/**
 * Handle chunk load error with automatic retry
 */
export const handleChunkLoadError = (error) => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  const attempts = getReloadAttempts();

  if (attempts < MAX_RELOAD_ATTEMPTS) {
    console.warn(
      `Chunk load error detected (attempt ${attempts + 1}/${MAX_RELOAD_ATTEMPTS}). Reloading page...`,
      error
    );

    incrementReloadAttempts();

    // Add a small delay to avoid immediate reload loop
    setTimeout(() => {
      // Navigate to root to avoid nested path issues after reload
      window.location.href = '/';
    }, 100);

    return true;
  } else {
    console.error(
      `Chunk load error persists after ${MAX_RELOAD_ATTEMPTS} attempts. Manual intervention required.`,
      error
    );
    resetReloadAttempts();
    return false;
  }
};

/**
 * Setup global error handlers for chunk loading errors
 */
export const setupChunkLoadErrorHandlers = () => {
  // Handle unhandled promise rejections (common for dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    if (handleChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });

  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (handleChunkLoadError(event.error)) {
      event.preventDefault();
    }
  });

  // Reset attempts counter on successful load
  window.addEventListener('load', () => {
    resetReloadAttempts();
  });
};
